import { environment } from 'environments/environment';

/** Legacy flat routes → unified feature paths (preserves nested segments). */
const LEGACY_FEATURE_PREFIX_REWRITES: ReadonlyArray<{ from: string; to: string }> = [
    { from: '/dashboard', to: '/overview' },
    { from: '/my-agents', to: '/ai-agents' },
    { from: '/autonomous-reports', to: '/reports/generated-reports' },
    { from: '/reports/autonomous-reports', to: '/reports/generated-reports' },
    { from: '/ai-forms', to: '/hyper-forms' },
    { from: '/chat-ai', to: '/ai-chat/agent-chat' },
    // SAI Master is production-hidden; legacy `/chat` lands on SAI Agent Chat in prod.
    {
        from: '/chat',
        to: environment.production
            ? '/ai-chat/agent-chat'
            : '/ai-chat/sai-master',
    },
];

/**
 * First path segments that were mounted under the legacy `/dashboards/` shell, taken from the
 * routing table as it stood before the flat-route migration.
 *
 * This is an allow-list on purpose. The rewrite used to strip the `/dashboards` prefix from ANY
 * unrecognised path, so `/dashboards/<anything>` became `/<anything>` and navigated somewhere the
 * user never asked for. That is also a trap for any FUTURE feature mounted under `/dashboards`:
 * its routes would be rewritten away before they could ever match, and the symptom (a link that
 * silently lands elsewhere) gives no hint where to look. An unknown segment now falls through
 * untouched instead.
 *
 * `dashboard` and `project` are deliberately absent: both are handled by their own branches in
 * `rewriteLegacyDashboardsPath` before this set is consulted.
 */
const LEGACY_DASHBOARDS_CHILD_SEGMENTS: ReadonlySet<string> = new Set([
    'activities',
    'add-tracker',
    'admin-agent-settings',
    'admin-api',
    'admin-contact',
    'admin-custom-agents',
    'admin-datasnap',
    'admin-users',
    'ai-forms',
    'analytics',
    'autonomous-reports',
    'chat',
    'chat-ai',
    'custom-agents',
    'data-sources',
    'datasnap',
    'faq',
    'forgot-password',
    'harvester',
    'html-generator',
    'integrations',
    'media',
    'models',
    'my-agents',
    'my-profile',
    'pages',
    'procedures',
    'profile',
    'reports',
    'roles-and-toolkits',
    'settings',
    'subscription',
    'support',
    'system-instructions',
    'tasks',
    'teams-chat',
    'terms',
    'test-chat',
    'tracker',
    'tutorial',
]);

/**
 * Rewrites a legacy `/dashboards/*` URL, or returns `null` when the path is not one.
 *
 * Returning `null` rather than the input unchanged is what lets the caller (and the guard above it)
 * tell "not mine" apart from "mine, and it maps to itself".
 */
function rewriteLegacyDashboardsPath(path: string): string | null {
    // Exact-or-boundary, so `/dashboardsomething` is not treated as a legacy dashboards URL.
    if (path !== '/dashboards' && !path.startsWith('/dashboards/')) {
        return null;
    }

    const remainder = path.slice('/dashboards'.length);
    // A BARE `/dashboards` is the Dashboards module's own list page and is no longer rewritten.
    //
    // It used to redirect to Overview, which was right for exactly as long as nothing lived at the
    // URL: it was the root of the pre-migration shell and there was no page to show. The module
    // (workspace ADR 0021) now owns it, and a rewrite here would send every reader of the list to
    // Overview before its route could match, which is the same trap `LEGACY_DASHBOARDS_CHILD_SEGMENTS`
    // was made an allow-list to avoid.
    //
    // Nothing is opened by this. `resolvePermissionKey` gates a URL the rewriter leaves alone on the
    // `dashboards` key, and that key is `disabled` for every existing tenant, so a pre-migration
    // bookmark to `/dashboards` still ends on Overview - now through the entitlement gate rather
    // than through this rewrite. A tenant that HOLDS the module gets the list, which is the point.
    if (!remainder || remainder === '/') {
        return null;
    }
    if (remainder === '/project' || remainder.startsWith('/project/')) {
        return '/overview';
    }
    if (remainder === '/dashboard' || remainder.startsWith('/dashboard/')) {
        return `/overview${remainder.slice('/dashboard'.length)}`;
    }

    const firstSegment = remainder.split('/')[1] ?? '';
    return LEGACY_DASHBOARDS_CHILD_SEGMENTS.has(firstSegment) ? remainder : null;
}

/**
 * Rewrites legacy data-source table URLs to canonical paths.
 * Mirror of `company/data-sources/data-source-table-route.util.ts`. `core/` stays
 * feature-agnostic, so the connector list is duplicated here rather than imported.
 * Keep both lists in sync when adding an external connector.
 */
function rewriteLegacyDataSourceTablesPath(path: string): string | null {
    const externalMatch = path.match(
        /^\/data-sources\/([^/]+)\/1\/(oracle|sap|Snowflake|remote-postgres)\/isExternal$/
    );
    if (externalMatch) {
        return `/data-sources/${externalMatch[1]}/external/${externalMatch[2]}`;
    }

    const tableMatch = path.match(/^\/data-sources\/([^/]+)\/1\/([^/]+)$/);
    if (tableMatch) {
        return `/data-sources/${tableMatch[1]}/tables/${tableMatch[2]}`;
    }

    const listMatch = path.match(/^\/data-sources\/([^/]+)\/1$/);
    if (listMatch) {
        return `/data-sources/${listMatch[1]}/tables`;
    }

    return null;
}

/**
 * Rewrites legacy app URLs (`/dashboards/*`, `/my-agents`, …) to flat unified paths.
 * Preserves query strings. Returns the input unchanged when no rewrite applies.
 */
export function rewriteLegacyAppUrl(fullUrl: string): string {
    const queryIndex = fullUrl.indexOf('?');
    const path = queryIndex >= 0 ? fullUrl.slice(0, queryIndex) : fullUrl;
    const querySuffix = queryIndex >= 0 ? fullUrl.slice(queryIndex) : '';

    let rewrittenPath = path;

    const dashboardsRewrite = rewriteLegacyDashboardsPath(path);
    if (dashboardsRewrite !== null) {
        rewrittenPath = dashboardsRewrite;
    }

    for (const { from, to } of LEGACY_FEATURE_PREFIX_REWRITES) {
        if (rewrittenPath === from || rewrittenPath.startsWith(`${from}/`)) {
            const remainder = rewrittenPath.slice(from.length);
            rewrittenPath = `${to}${remainder}`;
            break;
        }
    }

    const dataSourceTablesRewrite =
        rewriteLegacyDataSourceTablesPath(rewrittenPath);
    if (dataSourceTablesRewrite) {
        rewrittenPath = dataSourceTablesRewrite;
    }

    return `${rewrittenPath}${querySuffix}`;
}
